// Issue Trial dialog (BP8 verb #2) — server-issued trial, business-email-only.
//
// KISS: defaults pre-filled, the email/domain anti-abuse checks stay silent
// unless they fail (the backend returns 400/409 which we surface as one clear
// line). On success we show a receipt with a one-click "copy key" — never a raw
// JWT dumped on screen. POSTs to /api/v1/trials (BP6).
import React, { useState } from 'react';
import { apiRequest } from '@/lib/supabase';
import { Mail, Copy, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

interface IssueTrialResult {
  license_key: string;
  license_id: string;
  expires_at: string;
  is_trial: boolean;
}

export function IssueTrialDialog({
  open,
  onClose,
  defaultEmail = '',
  onIssued,
}: {
  open: boolean;
  onClose: () => void;
  defaultEmail?: string;
  onIssued?: () => void;
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [subject, setSubject] = useState<'platform' | 'module:ciic'>('platform');
  const [allowNonBusiness, setAllowNonBusiness] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IssueTrialResult | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setEmail(defaultEmail);
    setSubject('platform');
    setAllowNonBusiness(false);
    setOverrideReason('');
    setError(null);
    setResult(null);
    setCopied(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const issue = async () => {
    if (!email.trim()) {
      setError('A business email address is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { email: email.trim(), subject };
      if (allowNonBusiness) {
        body.allowNonBusinessEmail = true;
        if (overrideReason.trim()) body.overrideReason = overrideReason.trim();
      }
      const res = await apiRequest<IssueTrialResult>('/trials', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setResult(res);
      onIssued?.();
    } catch (err) {
      // Surfaces the backend's 400 (non-business email / validation) and 409
      // (already issued for this email×subject) as one clean line.
      setError(err instanceof Error ? err.message : 'Failed to issue trial.');
    } finally {
      setBusy(false);
    }
  };

  const copyKey = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.license_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', color: 'var(--text-primary)', background: 'var(--surface)', boxSizing: 'border-box' };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close(); }}>
      <DialogContent style={{ maxWidth: 460 }}>
        <DialogTitle style={{ fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Mail size={16} /> Issue Trial
        </DialogTitle>

        {result ? (
          // ── Receipt ──────────────────────────────────────────────────────
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--score-good)', fontWeight: 600, fontSize: 14, marginBottom: 10 }}>
              <CheckCircle2 size={18} /> Trial issued to {email.trim()}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
              Expires {new Date(result.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
              {' · '}key <span style={{ fontFamily: 'monospace' }}>{result.license_id}</span>
            </div>
            <button
              onClick={copyKey}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <Copy size={14} /> {copied ? 'Copied license key!' : 'Copy license key'}
            </button>
            <button
              onClick={close}
              style={{ width: '100%', marginTop: 10, padding: '10px 12px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
            >
              Done
            </button>
          </div>
        ) : (
          // ── Form ─────────────────────────────────────────────────────────
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={labelStyle}>Business email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@acme.com"
                style={inputStyle}
                autoFocus
              />
            </div>
            <div>
              <label style={labelStyle}>What to trial</label>
              <select value={subject} onChange={(e) => setSubject(e.target.value as any)} style={inputStyle}>
                <option value="platform">Platform (full product)</option>
                <option value="module:ciic">CIIC module only</option>
              </select>
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={allowNonBusiness} onChange={(e) => setAllowNonBusiness(e.target.checked)} style={{ marginTop: 2, accentColor: 'var(--accent)' }} />
              <span>Allow a non-business email (e.g. small biz on Gmail). Logs an override reason.</span>
            </label>
            {allowNonBusiness && (
              <input
                type="text"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Override reason (logged on the trial)"
                style={inputStyle}
              />
            )}

            {error && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: 'var(--ps-pill-red-bg)', border: '1px solid var(--ps-pill-red-tx)', borderRadius: 8, fontSize: 12.5, color: 'var(--ps-pill-red-tx)' }}>
                <AlertTriangle size={14} style={{ marginTop: 1, flexShrink: 0 }} />
                <span>{error}</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button
                onClick={issue}
                disabled={busy}
                style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: 'none', background: busy ? 'var(--accent-muted)' : 'var(--accent)', color: 'white', cursor: busy ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}
              >
                {busy ? 'Issuing…' : 'Issue Trial'}
              </button>
              <button onClick={close} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-raised)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
